import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { StableAvatar } from "@/components/ui/stable-avatar";
import { Button } from "@/components/ui/button";
import { PlusCircle, Crown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ProfileCreation } from "@/components/ProfileCreation";
import { ensureProfilesHaveAvatars, getRandomAvatar } from "@/lib/avatarUtils";
import { useProfileHierarchy } from "@/hooks/use-profile-hierarchy";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { motion, AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";

interface Profile {
  id: string;
  name: string;
  avatar_url: string | null;
  is_primary?: boolean;
  profile_type?: string;
}

interface ProfileSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileSelected?: (profile: Profile) => void;
}

export function ProfileSelectionModal({ isOpen, onClose, onProfileSelected }: ProfileSelectionModalProps) {
  const { session } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profiles: hierarchyProfiles, primaryProfile, switchProfile, isLoading: hierarchyLoading, fetchProfileHierarchy } = useProfileHierarchy();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch profiles when modal opens if hierarchy is empty
  useEffect(() => {
    if (isOpen && session?.user?.id && hierarchyProfiles.length === 0 && !hierarchyLoading) {
      // Trigger a refresh of the hierarchy
      fetchProfileHierarchy();
    }
  }, [isOpen, session?.user?.id, hierarchyProfiles.length, hierarchyLoading, fetchProfileHierarchy]);

  // Convert hierarchy profiles to the expected format
  useEffect(() => {
    if (hierarchyLoading) {
      setIsLoading(true);
      return;
    }
    
    if (hierarchyProfiles.length > 0) {
      const convertedProfiles = hierarchyProfiles.map(p => ({
        id: p.id,
        name: p.name,
        avatar_url: p.avatar_url,
        is_primary: p.is_primary,
        profile_type: p.profile_type
      }));
      setProfiles(convertedProfiles);
      setIsLoading(false);
    } else if (session?.user?.id) {
      // Fallback: fetch directly if hierarchy still empty
      fetchProfiles();
    } else {
      setIsLoading(false);
    }
  }, [hierarchyProfiles, hierarchyLoading, session?.user?.id]);

  const fetchProfiles = async () => {
    if (!session?.user?.id) return;
    
    setIsLoading(true);
    try {
      const { data: profilesData, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .order('is_primary', { ascending: false })
        .order('display_order', { ascending: true });

      if (error) {
        console.error("Error fetching profiles:", error);
        return;
      }

      let finalProfiles: Profile[] = [];

      if (profilesData && profilesData.length > 0) {
        const profilesWithAvatars = await ensureProfilesHaveAvatars(profilesData, session?.user?.id);
        finalProfiles = profilesWithAvatars;
      } else {
        // Create default profile if none exist
        const defaultProfile = {
          user_id: session.user.id,
          name: session?.user?.email?.split("@")[0] || "Main Profile",
          avatar_url: null,
          is_primary: true,
          profile_type: 'primary'
        };

        const { data: newProfile, error: createError } = await supabase
          .from('user_profiles')
          .insert(defaultProfile)
          .select()
          .single();

        if (!createError && newProfile) {
          const profilesWithAvatars = await ensureProfilesHaveAvatars([newProfile], session?.user?.id);
          finalProfiles = profilesWithAvatars;
        }
      }

      setProfiles(finalProfiles);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSelect = async (profileId: string) => {
    if (!session?.user?.id) return;
    
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) return;
    
    try {
      await switchProfile(profileId);
      
      // Mark profile selection as shown
      sessionStorage.setItem('profile-selection-shown', 'true');
      
      toast({
        title: "Profile Selected",
        description: `Welcome, ${profile.name}!`
      });
      
      onProfileSelected?.(profile);
      onClose();
    } catch (error) {
      console.error("Error selecting profile:", error);
      toast({
        title: "Error",
        description: "Failed to select profile",
        variant: "destructive"
      });
    }
  };

  const handleAddProfile = () => {
    if (profiles.length >= 5) {
      toast({
        title: "Maximum Profiles Reached",
        description: "You can have a maximum of 5 profiles per account.",
        variant: "destructive"
      });
      return;
    }
    
    setEditingProfile(null);
    setIsCreatingProfile(true);
  };

  const handleSaveProfile = async (profileId: string, name: string, avatarUrl: string | null, profileType?: string): Promise<void> => {
    if (!session?.user?.id) {
      throw new Error("No session");
    }

    // CRITICAL: Ensure we always have an avatar URL
    const finalAvatarUrl = avatarUrl || await getRandomAvatar();
    const existingProfile = profiles.find(p => p.id === profileId);
    
    if (existingProfile) {
      // Update existing profile - always include profile_type
      const updateData = {
        name,
        avatar_url: finalAvatarUrl,
        profile_type: profileType || 'standard',
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', profileId)
        .eq('user_id', session.user.id);

      if (error) {
        console.error("Error updating profile:", error);
        throw error;
      }
      
      toast({
        title: "Success",
        description: "Profile updated successfully"
      });
    } else {
      // Create new profile with correct profile_type
      const { error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: session.user.id,
          name,
          avatar_url: finalAvatarUrl,
          profile_type: profileType || 'standard',
          display_order: profiles.length
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating profile:", error);
        throw error;
      }
      
      toast({
        title: "Success",
        description: "Profile created successfully"
      });
    }

    // CRITICAL: Invalidate React Query cache for immediate UI update
    await queryClient.invalidateQueries({ queryKey: ['userProfiles', session.user.id] });
    
    // Refresh profiles from hierarchy
    await fetchProfileHierarchy();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full max-w-lg mx-auto bg-background/95 backdrop-blur-xl border border-border/50 shadow-2xl p-0 overflow-hidden z-[99999]" onPointerDownOutside={onClose}>
          <div className="relative">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
            
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="relative p-8"
              >
                <div className="text-center space-y-6">
                  <div className="space-y-3">
                    <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-4">
                      <Crown className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-2xl font-bold text-foreground">
                      Who's Reading?
                    </h2>
                    {primaryProfile && (
                      <p className="text-sm text-muted-foreground">
                        Account: {primaryProfile.name}
                      </p>
                    )}
                  </div>

                  {isLoading ? (
                    <div className="flex justify-center items-center h-32">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      {profiles.map((profile, index) => (
                        <motion.div
                          key={profile.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1, duration: 0.3 }}
                          className="group flex flex-col items-center gap-2 cursor-pointer transition-all p-3 rounded-xl hover:bg-background/50"
                          onClick={() => handleProfileSelect(profile.id)}
                        >
                          <div className="relative">
                            <StableAvatar 
                              avatarUrl={profile.avatar_url}
                              fallbackText={profile.name}
                              size="lg"
                              className="group-hover:scale-110 transition-transform duration-200"
                            />
                            {profile.is_primary && (
                              <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full p-1">
                                <Crown className="h-3 w-3 text-black" />
                              </div>
                            )}
                          </div>
                          <div className="text-center">
                            <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                              {profile.name}
                            </span>
                            {profile.profile_type === 'kids' && (
                              <div className="text-xs text-blue-500 mt-1">Kids</div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                      
                      {profiles.length < 5 && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: profiles.length * 0.1, duration: 0.3 }}
                          className="group flex flex-col items-center gap-2 cursor-pointer transition-all p-3 rounded-xl hover:bg-background/50"
                          onClick={handleAddProfile}
                        >
                          <div className="h-16 w-16 rounded-full bg-muted border-2 border-dashed border-muted-foreground/30 flex items-center justify-center group-hover:border-primary/50 group-hover:bg-primary/5 transition-all duration-200">
                            <PlusCircle className="h-8 w-8 text-muted-foreground group-hover:text-primary transition-colors" />
                          </div>
                          <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                            Add Profile
                          </span>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>

      <ProfileCreation 
        isOpen={isCreatingProfile}
        onClose={() => setIsCreatingProfile(false)}
        profileId={editingProfile?.id}
        initialName={editingProfile?.name || ""}
        initialAvatarUrl={editingProfile?.avatar_url}
        onSave={handleSaveProfile}
      />
    </>
  );
}